import { useCallback, useEffect, useMemo, useState } from 'react';
import { Space, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TableProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ROUTES } from '@/models/router.model';
import { BlogPostListSortField, IBlogPostListItem } from '@/models/blog-post.model';
import { StyledContentBlock } from '@/modules/Products/components/Styled';
import ContentPagesPostsFiltersContainer from '@/modules/ContentPages/containers/ContentPagesPostsFiltersContainer/ContentPagesPostsFiltersContainer';
import ContentPagesTableActions from '@/modules/ContentPages/components/ContentPagesTableActions/ContentPagesTableActions';
import { Button, Table } from '@/ui';
import { getListItemTitle } from '@/modules/ContentPages/helpers/blogPostMapper';
import { Modal, ModalSize, Pagination, showErrorToast } from '@/components';
import { useAppDispatch, useAppState } from '@/store';
import {
  contentPagesListSelector,
  deleteContentPage,
  getBlogCategories,
  getContentPagesList,
  setListPage,
  setListSort,
} from '@/modules/ContentPages';
import { useModal } from '@/hooks';
import { usePermissions, Resource } from '@/modules/Permissions';
import { DateFormats } from '@/constants/dates';
import { formatDate } from '@/utils';
import {
  StyledButtonsWrapper,
  ContentPagesBlogTableWrap,
  StyledListHeaderBlock,
  StyledListPageTitle,
  StyledPaginationWrap,
  StyledTitleModal,
} from './ContentPagesPostsTabStyles';

const SORTABLE_FIELDS: BlogPostListSortField[] = ['published_at', 'published', 'is_pinned', 'created_at'];

type ContentPagesTableOnChange = NonNullable<TableProps<IBlogPostListItem>['onChange']>;

const authorLabel = (record: IBlogPostListItem): string => {
  const a = record.author;
  if (!a) return '—';
  if (a.full_name?.trim()) return a.full_name.trim();
  const parts = [a.first_name, a.last_name].filter(Boolean).join(' ').trim();
  return parts || '—';
};

const ContentPagesPostsTab = (): JSX.Element => {
  const { t, i18n } = useTranslation(['common']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { canManage } = usePermissions();
  const canManageContent = canManage(Resource.CONTENT);
  const { isOpened, openModal, closeModal } = useModal();
  const [postToDelete, setPostToDelete] = useState<IBlogPostListItem | null>(null);

  const {
    items,
    isLoading,
    currentPage,
    total,
    pageSize,
    blogCategoryId,
    search,
    searchId,
    searchAuthor,
    sortField,
    sortDirection,
  } = useAppState(contentPagesListSelector);

  useEffect(() => {
    dispatch(getBlogCategories())
      .unwrap()
      .catch(() => {});
  }, [dispatch]);

  useEffect(() => {
    dispatch(getContentPagesList())
      .unwrap()
      .catch(() => showErrorToast({ message: t('common_error') }));
  }, [currentPage, blogCategoryId, search, searchId, searchAuthor, sortField, sortDirection, dispatch, t]);

  const handleCreate = () => {
    navigate(ROUTES.CONTENT_PAGE_EXAMPLE);
  };

  const requestDelete = useCallback(
    (record: IBlogPostListItem) => {
      setPostToDelete(record);
      openModal();
    },
    [openModal],
  );

  const handleCloseDeleteModal = useCallback(() => {
    closeModal();
    setPostToDelete(null);
  }, [closeModal]);

  const handleConfirmDelete = async () => {
    if (!postToDelete) return;
    try {
      await dispatch(deleteContentPage({ id: postToDelete.id })).unwrap();
      await dispatch(getContentPagesList()).unwrap();
      handleCloseDeleteModal();
    } catch {
      showErrorToast({ message: t('common_error') });
    }
  };

  const sortOrderFor = useCallback(
    (field: BlogPostListSortField) =>
      sortField === field ? (sortDirection === 'asc' ? 'ascend' : 'descend') : undefined,
    [sortDirection, sortField],
  );

  const handleTableChange = useCallback<ContentPagesTableOnChange>(
    (_pagination, _filters, sorter) => {
      const col = Array.isArray(sorter) ? sorter[0] : sorter;
      const key = col?.columnKey as BlogPostListSortField | undefined;
      if (!key || !SORTABLE_FIELDS.includes(key)) return;
      if (!col?.order) {
        dispatch(setListSort({ field: 'created_at', direction: 'desc' }));
        return;
      }
      dispatch(
        setListSort({
          field: key,
          direction: col.order === 'ascend' ? 'asc' : 'desc',
        }),
      );
    },
    [dispatch],
  );

  const columns: ColumnsType<IBlogPostListItem> = useMemo(
    () => [
      {
        title: t('content_pages_list_column_id'),
        dataIndex: 'id',
        key: 'id',
        ellipsis: true,
      },
      {
        title: t('content_pages_list_column_category'),
        key: 'blog_category',
        render: (_, record) => record.blog_category?.name ?? '—',
      },
      {
        title: t('content_pages_list_column_date'),
        dataIndex: 'published_at',
        key: 'published_at',
        sorter: true,
        sortOrder: sortOrderFor('published_at'),
        render: (v: string | null | undefined) => (v ? formatDate(v, DateFormats.EUROPEAN) : '—'),
      },
      {
        title: t('content_pages_list_column_author'),
        key: 'author',
        render: (_, record) => authorLabel(record),
      },
      {
        title: t('content_pages_list_column_title'),
        key: 'title',
        render: (_, record) => (
          <Space size={8}>
            <span>{getListItemTitle(record, i18n.language)}</span>
            {record.is_draft ? <Tag color="red">{t('content_pages_draft_badge')}</Tag> : null}
          </Space>
        ),
      },
      {
        title: t('content_pages_list_column_published'),
        dataIndex: 'published',
        key: 'published',
        sorter: true,
        sortOrder: sortOrderFor('published'),
        render: (v: boolean) => (v ? t('content_pages_bool_yes') : t('content_pages_bool_no')),
      },
      {
        title: t('content_pages_list_column_pinned'),
        dataIndex: 'is_pinned',
        key: 'is_pinned',
        sorter: true,
        sortOrder: sortOrderFor('is_pinned'),
        render: (v: boolean) => (v ? t('content_pages_bool_yes') : t('content_pages_bool_no')),
      },
      {
        title: t('content_pages_list_column_actions'),
        key: 'actions',
        render: (_, record) => (
          <ContentPagesTableActions record={record} onRequestDelete={requestDelete} canManage={canManageContent} />
        ),
      },
    ],
    [canManageContent, i18n.language, requestDelete, sortOrderFor, t],
  );

  return (
    <>
      <StyledListHeaderBlock>
        <StyledListPageTitle>{t('content_pages_tab_blog')}</StyledListPageTitle>
        {canManageContent && (
          <Button type="primary" onClick={handleCreate}>
            {t('content_pages_list_action_create')}
          </Button>
        )}
      </StyledListHeaderBlock>
      <StyledContentBlock>
        <ContentPagesBlogTableWrap>
          <Table<IBlogPostListItem>
            rowKey="id"
            loading={isLoading}
            dataSource={items}
            columns={columns}
            scroll={{ x: 'max-content' }}
            pagination={false}
            onChange={handleTableChange}
            components={{
              header: {
                wrapper: ContentPagesPostsFiltersContainer,
              },
            }}
          />
        </ContentPagesBlogTableWrap>

        <StyledPaginationWrap>
          <Pagination
            hideOnSinglePage
            current={currentPage}
            pageSize={pageSize}
            total={total}
            onChange={(p) => dispatch(setListPage(p))}
          />
        </StyledPaginationWrap>

        <Modal visible={isOpened} onCancel={handleCloseDeleteModal} size={ModalSize.LARGE}>
          <StyledTitleModal>{t('content_pages_delete_modal_title')}</StyledTitleModal>
          <StyledButtonsWrapper>
            <Button onClick={handleCloseDeleteModal} loading={isLoading} type="primary">
              {t('content_pages_delete_modal_cancel')}
            </Button>
            <Button onClick={handleConfirmDelete} loading={isLoading} type="text">
              {t('content_pages_delete_modal_confirm')}
            </Button>
          </StyledButtonsWrapper>
        </Modal>
      </StyledContentBlock>
    </>
  );
};

export default ContentPagesPostsTab;
